import { useMemo } from 'react';
import { useQueries } from '@tanstack/react-query';
import { isInternal } from '@/utils/keycloakRoles';
import { fetchPlantonCertificates, type PlantonCertificate } from '@/integrations/agreements/client';
import { useAppSelector } from '@/store/hooks';

export type { PlantonCertificate };

/** Ids por requisição no realm `musa`: abaixo do teto da rota (1000) e longe do limite de URL. */
export const PLANTON_IDS_PER_REQUEST = 200;

const STALE = 5 * 60 * 1000;

function chunk<T>(items: T[], size: number): T[][] {
  const chunks: T[][] = [];
  for (let i = 0; i < items.length; i += size) chunks.push(items.slice(i, i + size));
  return chunks;
}

/**
 * Certificados Planton disponíveis para a aba Certificados.
 *
 * - Gerador externo: **uma** requisição sem `generatorId` — o agreements devolve todo o escopo do
 *   token. O filtro da tela (geradores/unidades) recorta no cliente. Antes era uma requisição por
 *   filial (832 numa carteira grande), e a aba ficava carregando sem fim.
 * - Realm `musa`: só com seleção (sem ela a tela pede para escolher um gerador), em blocos de
 *   `PLANTON_IDS_PER_REQUEST` ids por requisição.
 *
 * A chave de cache usa os ids ordenados: a mesma seleção em outra ordem não refaz a chamada.
 */
export function usePlantonCertificates(
  selectedGeneratorExtIds: string[] = [],
  selectedEstablishmentInternalIds: string[] = [],
) {
  const internal = isInternal();
  const storeGenerators = useAppSelector((s) => s.establishmentAccess.generators);

  const selectedIds = useMemo(() => {
    const ids = new Set<number>();
    for (const extId of selectedGeneratorExtIds) {
      const id = storeGenerators.find((g) => g.external_id === extId || g.id === extId)?.id;
      if (id) ids.add(Number(id));
    }
    for (const id of selectedEstablishmentInternalIds) {
      if (id) ids.add(Number(id));
    }
    return [...ids].filter((id) => !Number.isNaN(id)).sort((a, b) => a - b);
  }, [selectedGeneratorExtIds, selectedEstablishmentInternalIds, storeGenerators]);

  const queries = useQueries({
    queries: internal
      ? chunk(selectedIds, PLANTON_IDS_PER_REQUEST).map((ids) => ({
          queryKey: ['planton-certificates', 'musa', ids] as const,
          queryFn: () => fetchPlantonCertificates(ids),
          staleTime: STALE,
        }))
      : [
          {
            queryKey: ['planton-certificates', 'generator'] as const,
            queryFn: () => fetchPlantonCertificates(),
            staleTime: STALE,
          },
        ],
  });

  const isLoading = queries.some((q) => q.isLoading);
  const certificates = useMemo(() => {
    const selected = new Set(selectedIds.map(String));
    return queries
      .flatMap((q) => q.data ?? [])
      .filter((c) => c.status === 'disponivel')
      .filter((c) => internal || selected.size === 0 || selected.has(c.generator_id));
  }, [queries, selectedIds, internal]);

  return { certificates, isLoading };
}
